import type { Icon } from "@tabler/icons-react";
import { DESIGN_ICONS } from "../../theme/designIcons";
import type { FeatureKey, SectionPermissions } from "../../types/featureAccess";
import { hasAnyReportAccess } from "../../utils/reportAccess";
import {
  getVisibleSalesTabs,
  SALES_BASE,
  salesTabRoute,
} from "../../utils/salesAccess";

/**
 * The grouped navigation (calendar redesign handoff §11 "Navigation"): the
 * rail shows one button per section, and each section opens a flyout (desktop)
 * or an accordion (mobile) listing its pages with a short description.
 *
 * Pure: the desktop rail, the mobile nav sheet and the tests all build from
 * `buildNavSections`, so visibility rules live in exactly one place.
 */

export const LOCKED_TOOLTIP = "Upgrade to Business Essentials for access";

export type NavSectionKey =
  | "core"
  | "growth"
  | "analytics"
  | "payments"
  | "payroll";

export interface NavLink {
  key: string;
  label: string;
  /** One-line subtext under the label in the flyout. */
  description: string;
  icon: Icon;
  path: string;
  /** Route prefix that keeps the link active on sub-routes (defaults to `path`). */
  match?: string;
  /** Shown but not clickable: the subscription tier doesn't include it. */
  locked?: boolean;
}

export interface NavSection {
  key: NavSectionKey;
  label: string;
  icon: Icon;
  links: NavLink[];
}

/**
 * `voice_ai` is filled in by FeatureAccessContext's dev-only gate but isn't in
 * the backend's FeatureKey union yet.
 */
export type NavFeature = FeatureKey | "voice_ai";

export interface NavAccess {
  /** `/access` section permissions (fail-closed: undefined = no access). */
  perms: Partial<SectionPermissions> | null | undefined;
  hasFeature: (feature: NavFeature) => boolean;
  isOwner: boolean;
  isOwnerOrManager: boolean;
  /** settingsPayroll permission AND payroll set up for this user (payrollAccess). */
  canAccessPayroll: boolean;
  /** REACT_APP_SALES_TAB: Sales replaces the standalone Cash Drawer entry. */
  salesTabEnabled: boolean;
  /** REACT_APP_GIFT_CARDS_ENABLED: adds the (ungated) Sales → Gift Cards tab. */
  giftCardsEnabled: boolean;
  /** Payments pages exist only once the Stripe Connect account is onboarded. */
  stripeOnboarded: boolean;
}

type MaybeLink = NavLink | false | null | undefined;

const links = (...items: MaybeLink[]): NavLink[] =>
  items.filter((item): item is NavLink => !!item);

export function buildNavSections(access: NavAccess): NavSection[] {
  const {
    perms,
    hasFeature,
    isOwner,
    isOwnerOrManager,
    canAccessPayroll,
    salesTabEnabled,
    giftCardsEnabled,
    stripeOnboarded,
  } = access;
  const reportsPerms = perms?.reports;
  // Sales shows only when the user can open at least one of its sub-tabs, and
  // lands on the first of them (salesAccess), so it never opens on a tab
  // whose data request the API would refuse.
  const salesTabs = salesTabEnabled
    ? getVisibleSalesTabs(perms, { giftCardsEnabled })
    : [];

  const sections: NavSection[] = [
    {
      key: "core",
      label: "Core",
      icon: DESIGN_ICONS.House,
      links: links(
        {
          key: "calendar",
          label: "Calendar",
          description: "Appointments & schedules",
          icon: DESIGN_ICONS.Calendar,
          path: "/admin/calendar",
        },
        // Sales lands on its first open sub-tab (Transactions when allowed);
        // the rest are reached from SalesPage's in-page tab bar.
        salesTabs.length > 0 && {
          key: "sales",
          label: "Sales",
          description: "Checkout & transactions",
          icon: DESIGN_ICONS.Receipt,
          path: salesTabRoute(salesTabs[0]),
          match: SALES_BASE,
        },
        // With the Sales tab off, Cash Drawer keeps its standalone entry.
        !salesTabEnabled &&
          perms?.cashRegister?.canView === true && {
            key: "cash-drawer",
            label: "Cash Drawer",
            description: "Drawer counts & cash sessions",
            icon: DESIGN_ICONS.CashRegister,
            path: "/admin/cash-drawer",
          },
        perms?.clients?.canView === true && {
          key: "clients",
          label: "Clients",
          description: "Client list & individual details",
          icon: DESIGN_ICONS.UsersRound,
          path: "/admin/clients",
        },
        perms?.products?.canView === true && {
          key: "products",
          label: "Products",
          description: "Inventory & retail",
          icon: DESIGN_ICONS.Box,
          path: "/admin/products",
        },
      ),
    },
    {
      key: "growth",
      label: "Growth",
      icon: DESIGN_ICONS.TrendingUp,
      links: links(
        perms?.marketing?.canView === true && {
          key: "marketing",
          label: "Marketing",
          description: "Campaigns & automations",
          icon: DESIGN_ICONS.Megaphone,
          path: "/admin/marketing",
          locked: !hasFeature("marketing"),
        },
        // Hidden entirely unless the loyalty feature is available.
        hasFeature("loyalty") && {
          key: "loyalty",
          label: "Loyalty",
          description: "Points & rewards",
          icon: DESIGN_ICONS.Gift,
          path: "/admin/loyalty/rewards",
          match: "/admin/loyalty",
        },
        // Owner/Manager only; the dev-only gate lives in FeatureAccessContext.
        isOwnerOrManager &&
          hasFeature("voice_ai") && {
            key: "voice-ai",
            label: "Voice AI",
            description: "AI receptionist",
            icon: DESIGN_ICONS.AudioLines,
            path: "/admin/voice-ai",
          },
      ),
    },
    {
      key: "analytics",
      label: "Analytics",
      icon: DESIGN_ICONS.ChartColumn,
      links: links(
        hasAnyReportAccess(reportsPerms) && {
          key: "reports",
          label: "Summaries & Reports",
          description: "Daily Summaries, Earnings Reports, etc...",
          icon: DESIGN_ICONS.FileChartColumn,
          path: "/admin/reports",
        },
      ),
    },
    {
      key: "payments",
      label: "Payments",
      icon: DESIGN_ICONS.CreditCard,
      links:
        stripeOnboarded && perms?.payments?.canView === true
          ? links(
              {
                key: "payments-overview",
                label: "Overview",
                description: "Payouts & balances",
                icon: DESIGN_ICONS.Landmark,
                path: "/admin/payments/overview",
              },
              {
                key: "payments-devices",
                label: "Devices",
                description: "Card readers & terminals",
                icon: DESIGN_ICONS.TabletSmartphone,
                path: "/admin/payments/devices",
              },
              // Stripe account management & documents: Owners only.
              isOwner && {
                key: "payments-settings",
                label: "Stripe Settings",
                description: "Stripe account & documents",
                icon: DESIGN_ICONS.SlidersHorizontal,
                path: "/admin/payments/settings",
              },
            )
          : [],
    },
    {
      key: "payroll",
      label: "Payroll",
      icon: DESIGN_ICONS.Wallet,
      links: links(
        canAccessPayroll && {
          key: "payroll-overview",
          label: "Overview",
          description: "Pay runs & commissions",
          icon: DESIGN_ICONS.HandCoins,
          path: "/admin/payroll",
          locked: !hasFeature("payroll"),
        },
      ),
    },
  ];

  // A section with nothing visible in it has no rail button.
  return sections.filter((section) => section.links.length > 0);
}

export const isNavLinkActive = (link: NavLink, pathname: string): boolean =>
  pathname.startsWith(link.match ?? link.path);

/** The section holding the current page, if any (marks the rail button). */
export const findActiveSection = (
  sections: NavSection[],
  pathname: string,
): NavSection | undefined =>
  sections.find((section) =>
    section.links.some((link) => isNavLinkActive(link, pathname)),
  );
